"use client";

import Image from "next/image";
import { LayoutGrid } from "lucide-react";

import { BROWSE_CATEGORIES, CATEGORY_PHOTO_IDS } from "@/components/feed/browse-categories";
import { SectionHeader } from "@/components/feed/section-header";
import { cn } from "@/lib/utils";

/**
 * Vivid per-category placeholder gradient (inline so new colors always paint —
 * Tailwind's dev build skips unused color/gradient classes). Fallback for any
 * FUTURE category added to `BROWSE_CATEGORIES` before its cover photo lands —
 * every current category already has a `CATEGORY_PHOTOS` entry below.
 */
const CATEGORY_GRADIENT_FALLBACK = "linear-gradient(135deg, #525252 0%, #0a0a0a 100%)";

/*
 * Photo-backed ids live in the shared taxonomy module (`CATEGORY_PHOTO_IDS`,
 * browse-categories.ts) — same set drives these tiles, the BROWSE sidebar rows
 * and the category coming-soon medallion. Styling rationale for THIS surface:
 * dark card + drop-shadow (not box-shadow — it hugs the cutout's alpha
 * silhouette) makes each photo float like the reference mock; comics/
 * trading-cards are opaque shots of an inherently rectangular graded slab, so
 * their rectangular contact shadow still reads as natural. New photos ship as
 * webp q82, longest side ≤ 1200px (@see .tondocs/images/
 * HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §3).
 */

/**
 * ShopByCategory — the landing's category tile grid (mockup: Kraahl Desktop
 * Home, 6 tiles). Reuses the BROWSE taxonomy (minus "All items"); each tile
 * reports its category id via `onSelectAction` (same target as the sidebar).
 * Categories with a real cover photo (`CATEGORY_PHOTOS`) show it floating with
 * a drop-shadow on a neutral card; the rest keep the vivid gradient + faded
 * glyph placeholder until their photo shoot lands.
 */
export function ShopByCategory({
  onSelectAction,
  className,
}: {
  onSelectAction: (id: string) => void;
  className?: string;
}) {
  const tiles = BROWSE_CATEGORIES.filter((c) => c.id !== "all");
  return (
    <div className={className}>
      <SectionHeader icon={LayoutGrid} title="Shop by category" />
      <div className="grid grid-cols-3 gap-3.5 sm:grid-cols-6">
        {tiles.map(({ id, label, shortLabel, icon: Icon }) => {
          const hasPhoto = CATEGORY_PHOTO_IDS.has(id);
          const tileLabel = shortLabel ?? label;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelectAction(id)}
              className={cn(
                "group relative aspect-[5/4] overflow-hidden rounded-2xl border border-black/10 bg-muted dark:border-white/10",
                "transition-transform hover:-translate-y-0.5 active:scale-[0.98]",
              )}
            >
              {hasPhoto ? (
                <>
                  {/* Foil-tinted "studio stage" — theme-aware authored surface
                      (globals.css `.category-stage` + `.dark` override): pastel
                      pink/cyan foil hues over a light card in light mode, the
                      lifted-black version in dark mode. */}
                  <div className="category-stage absolute inset-0" />
                  {/* Inline inset (not inset-x-3/bottom-9 classes): those exact
                      offsets aren't used anywhere else in the app, and Tailwind
                      v4's dev build skips emitting a utility combo no other
                      file references yet — the tile silently collapsed to 0x0
                      until this was inlined. */}
                  <div
                    className="absolute"
                    style={{ left: "0.75rem", right: "0.75rem", top: "0.5rem", bottom: "2.25rem" }}
                  >
                    {/* `unoptimized` is LOAD-BEARING: these six sources are
                        already at the project's serve-ready standard (webp q82,
                        ≤1200px, 30–160 KB), so the optimizer adds nothing — and
                        it actively broke this section: sharp's AVIF encoder
                        HANGS (never responds) on specific (file, width) cells
                        of the variant matrix, e.g. lego.webp at w=640 — the
                        exact variant a Retina viewport requests. Result: black
                        tiles on DPR-2 screens while DPR-1 (other widths) was
                        fine. Serving the original file kills the whole variant
                        matrix, and skips a per-variant billed transformation in
                        prod for static marketing assets. */}
                    <Image
                      src={`/categories/${id}.webp`}
                      alt=""
                      fill
                      unoptimized
                      className="object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.55)] transition-transform group-hover:scale-105"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="absolute inset-0"
                    style={{ backgroundImage: CATEGORY_GRADIENT_FALLBACK }}
                  />
                  <Icon
                    className="absolute -right-1 -top-1 size-11 text-white/20 transition-transform group-hover:scale-110"
                    strokeWidth={1.5}
                  />
                </>
              )}
              {/* Bottom scrim keeps the label legible over either treatment —
                  theme-aware (white fade in light, black fade in dark). */}
              <div className="category-scrim absolute inset-0" />
              <span className="absolute bottom-2.5 left-3 text-sm font-semibold text-neutral-900 drop-shadow-sm dark:text-white">
                {tileLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
